/**
 * geofenceRadius — the stepped radius scale shared by every geofence surface
 * (the map picker, the account "Default radius" row, the location "Rules"
 * row). One home so the account and location sides offer the same steps and
 * print the same label for a value.
 */
import React from 'react';
import { textRole, tokenVars } from '@turn-connect/ui';

const FEET_PER_METER = 3.28084;
const METERS_PER_MILE = 1609.34;

/** Metres; 100 ft … 50 miles. */
export const RADIUS_STEPS = [30, 61, 91, 152, 305, 402, 805, 1609, 3219, 4828, 8047, 16093, 24140, 32187, 40234, 48280, 64374, 80467];

export const formatRadius = (meters: number): string => {
  const feet = meters * FEET_PER_METER;
  // The steps are whole-metre roundings of mile marks (1609 m ≈ 0.9998 mi):
  // round to a tenth first so the label reads "1 mile", not "1.0 miles".
  const miles = Math.round((meters / METERS_PER_MILE) * 10) / 10;
  if (miles >= 0.5) {
    return miles === Math.floor(miles) ? `${Math.floor(miles)} mile${miles !== 1 ? 's' : ''}` : `${miles.toFixed(1)} miles`;
  }
  return `${Math.round(feet).toLocaleString()} feet`;
};

export const findClosestStepIndex = (meters: number): number => {
  let best = 0;
  for (let i = 1; i < RADIUS_STEPS.length; i++) {
    if (Math.abs(RADIUS_STEPS[i] - meters) < Math.abs(RADIUS_STEPS[best] - meters)) best = i;
  }
  return best;
};

export interface RadiusSliderProps {
  radiusMeters: number;
  onRadiusChange: (m: number) => void;
  disabled?: boolean;
  /** Caption above the slider; omit to render the slider alone. */
  label?: string;
  testId?: string;
}

export const RadiusSlider: React.FC<RadiusSliderProps> = ({
  radiusMeters,
  onRadiusChange,
  disabled,
  label = 'Geofence Radius',
  testId,
}) => {
  const stepIndex = findClosestStepIndex(radiusMeters);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={textRole('meta', 'secondary')}>{label}</span>
        <span style={{ ...textRole('meta', 'primary'), fontWeight: 500 }} data-testid={testId ? `${testId}-value` : undefined}>
          {formatRadius(radiusMeters)}
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={RADIUS_STEPS.length - 1}
        step={1}
        value={stepIndex}
        onChange={(e) => onRadiusChange(RADIUS_STEPS[parseInt(e.target.value, 10)])}
        disabled={disabled}
        aria-label={label}
        data-testid={testId}
        style={{ width: '100%', minHeight: 44, accentColor: tokenVars.brandFill, cursor: disabled ? 'not-allowed' : 'pointer' }}
      />
      <div style={{ ...textRole('meta'), display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span>100 ft</span>
        <span>50 miles</span>
      </div>
    </div>
  );
};

export default RadiusSlider;
